import { useState } from "react";

import { useNavigate } from "react-router-dom";

import dayjs from "dayjs";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Clock,
  Package,
  PackageX,
  RefreshCw,
  RotateCcw,
  Search,
  Trash2,
  Truck,
  User,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useToast } from "~/hooks/use-toast";
import {
  useClaimReturnRequest,
  useInProgressReturnRequests,
  usePendingReturnRequests,
  useReturnRequests,
  useReturnStatistics,
} from "~/hooks/wms";
import { useProfileStore } from "~/store/profileStore";
import type {
  DispositionType,
  ReturnRequest,
  ReturnRequestStatus,
} from "~/types/returns";
import {
  DISPOSITION_LABELS,
  RETURN_REQUEST_STATUS_LABELS,
  RETURN_TYPE_LABELS,
} from "~/types/returns";

const statusConfig: Record<
  ReturnRequestStatus,
  {
    icon: React.ElementType;
    variant: "default" | "secondary" | "destructive" | "outline";
    color: string;
  }
> = {
  pending: { icon: Clock, variant: "secondary", color: "text-yellow-500" },
  receiving: { icon: PackageX, variant: "default", color: "text-blue-500" },
  inspecting: { icon: Search, variant: "default", color: "text-purple-500" },
  routing: { icon: ArrowRight, variant: "default", color: "text-orange-500" },
  completed: {
    icon: CheckCircle2,
    variant: "outline",
    color: "text-green-500",
  },
  cancelled: {
    icon: AlertCircle,
    variant: "destructive",
    color: "text-red-500",
  },
};

export function ReturnsPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { id: userId } = useProfileStore();
  const [selectedRequest, setSelectedRequest] = useState<ReturnRequest | null>(
    null
  );
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);

  const { data: pendingRequests = [], isLoading: pendingLoading } =
    usePendingReturnRequests();
  const { data: inProgressRequests = [], isLoading: inProgressLoading } =
    useInProgressReturnRequests();
  const { data: stats } = useReturnStatistics();
  const { data: completedRequests = [] } = useReturnRequests({
    status: "completed",
    limit: 5,
  });
  const claimMutation = useClaimReturnRequest();

  const handleClaimRequest = (request: ReturnRequest) => {
    setSelectedRequest(request);
    setClaimDialogOpen(true);
  };

  const confirmClaim = (startReceiving = false) => {
    if (!selectedRequest || !userId) return;

    claimMutation.mutate(
      { id: selectedRequest.id, userId },
      {
        onSuccess: () => {
          toast({
            title: "Devolución asignada",
            description: "La devolución ha sido asignada a ti",
          });
          setClaimDialogOpen(false);

          if (startReceiving) {
            navigate(`/wms/returns/${selectedRequest.id}/receive`);
          }
          setSelectedRequest(null);
        },
        onError: (error) => {
          toast({
            title: "Error",
            description: `No se pudo asignar la devolución: ${error.message}`,
            variant: "destructive",
          });
        },
      }
    );
  };

  const handleContinue = (request: ReturnRequest) => {
    const routes: Record<ReturnRequestStatus, string> = {
      pending: `/wms/returns/${request.id}`,
      receiving: `/wms/returns/${request.id}/receive`,
      inspecting: `/wms/returns/${request.id}/inspect`,
      routing: `/wms/returns/${request.id}/putaway`,
      completed: `/wms/returns/${request.id}`,
      cancelled: `/wms/returns/${request.id}`,
    };
    navigate(routes[request.status]);
  };

  const isLoading = pendingLoading || inProgressLoading;

  return (
    <PageContainer>
      <PageHeader
        title="Devoluciones"
        description="Gestión de devoluciones de productos y notas de crédito"
        back={
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-9 text-muted-foreground"
            onClick={() => navigate("/wms")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        }
      />

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pendientes</p>
                  <p className="text-3xl font-bold">{stats.pending_returns}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">En Proceso</p>
                  <p className="text-3xl font-bold">
                    {stats.receiving_returns +
                      stats.inspecting_returns +
                      stats.routing_returns}
                  </p>
                </div>
                <RefreshCw className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Completados Hoy
                  </p>
                  <p className="text-3xl font-bold">{stats.completed_today}</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Items Pendientes
                  </p>
                  <p className="text-3xl font-bold">
                    {stats.total_items_pending.toLocaleString()}
                  </p>
                </div>
                <Package className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Disposition Breakdown */}
      {stats && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Resumen de Disposiciones
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-3">
                <RotateCcw className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-xs text-green-600">Vendible (A)</p>
                  <p className="text-xl font-bold text-green-700">
                    {stats.disposition_breakdown.sellable}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                <Package className="h-5 w-5 text-yellow-600" />
                <div>
                  <p className="text-xs text-yellow-600">Re-empacar (B)</p>
                  <p className="text-xl font-bold text-yellow-700">
                    {stats.disposition_breakdown.repackage}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-3">
                <Trash2 className="h-5 w-5 text-red-600" />
                <div>
                  <p className="text-xs text-red-600">Descartar (C)</p>
                  <p className="text-xl font-bold text-red-700">
                    {stats.disposition_breakdown.discard}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-lg border border-purple-200 bg-purple-50 p-3">
                <Truck className="h-5 w-5 text-purple-600" />
                <div>
                  <p className="text-xs text-purple-600">Dev. Proveedor (D)</p>
                  <p className="text-xl font-bold text-purple-700">
                    {stats.disposition_breakdown.supplier_return}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Requests */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="h-4 w-4" />
            Devoluciones Pendientes ({pendingRequests.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : pendingRequests.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              No hay devoluciones pendientes
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>N° Devolución</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Nota Crédito</TableHead>
                  <TableHead>Prioridad</TableHead>
                  <TableHead className="w-[100px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingRequests.map((request, index) => (
                  <ReturnRequestRow
                    key={request.id}
                    request={request}
                    index={index}
                    onClaim={handleClaimRequest}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* In Progress Requests */}
      {inProgressRequests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <RefreshCw className="h-4 w-4" />
              En Proceso ({inProgressRequests.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>N° Devolución</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Progreso</TableHead>
                  <TableHead>Asignado a</TableHead>
                  <TableHead>Iniciado</TableHead>
                  <TableHead className="w-[100px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inProgressRequests.map((request, index) => {
                  const config = statusConfig[request.status];
                  const StatusIcon = config.icon;
                  const itemsCount = request.items?.length || 0;
                  const completedItems =
                    request.items?.filter((i) => i.status === "routed")
                      .length || 0;

                  return (
                    <TableRow
                      key={request.id}
                      style={{
                        animationDelay: `${Math.min(index * 40, 400)}ms`,
                      }}
                      className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                    >
                      <TableCell className="font-medium">
                        {request.return_number}
                      </TableCell>
                      <TableCell>{request.client?.name || "-"}</TableCell>
                      <TableCell>
                        <Badge variant={config.variant}>
                          <StatusIcon className="mr-1 h-3 w-3" />
                          {RETURN_REQUEST_STATUS_LABELS[request.status]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {completedItems}/{itemsCount}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            items
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {request.assigned_profile?.name &&
                          request.assigned_profile?.lastname
                            ? `${request.assigned_profile.name} ${request.assigned_profile.lastname}`
                            : "-"}
                        </div>
                      </TableCell>
                      <TableCell>
                        {request.started_at
                          ? dayjs(request.started_at).format("DD/MM HH:mm")
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleContinue(request)}
                        >
                          Continuar
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Recent Completed */}
      {completedRequests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CheckCircle2 className="h-4 w-4" />
              Completados Recientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>N° Devolución</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Completado por</TableHead>
                  <TableHead>Fecha</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {completedRequests.map((request, index) => (
                  <TableRow
                    key={request.id}
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                    className="cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted/50"
                    onClick={() => navigate(`/wms/returns/${request.id}`)}
                  >
                    <TableCell className="font-medium">
                      {request.return_number}
                    </TableCell>
                    <TableCell>{request.client?.name || "-"}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {RETURN_TYPE_LABELS[request.return_type]}
                      </Badge>
                    </TableCell>
                    <TableCell>{request.items?.length || 0} items</TableCell>
                    <TableCell>
                      {request.assigned_profile?.name &&
                      request.assigned_profile?.lastname
                        ? `${request.assigned_profile.name} ${request.assigned_profile.lastname}`
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {request.completed_at
                        ? dayjs(request.completed_at).format("DD/MM/YYYY HH:mm")
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Claim Dialog */}
      <Dialog open={claimDialogOpen} onOpenChange={setClaimDialogOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Tomar Devolución</DialogTitle>
            <DialogDescription>
              ¿Desea asignarse esta devolución para procesarla?
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4 py-4">
              <div className="space-y-1">
                <span className="text-xs uppercase tracking-wide text-muted-foreground">
                  N° Devolución
                </span>
                <p className="text-sm font-medium">
                  {selectedRequest.return_number}
                </p>
              </div>
              <div className="space-y-1">
                <span className="text-xs uppercase tracking-wide text-muted-foreground">
                  Cliente
                </span>
                <p className="text-sm font-medium">
                  {selectedRequest.client?.name || "-"}
                </p>
              </div>
              {selectedRequest.credit_note_number && (
                <div className="space-y-1">
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">
                    Nota de Crédito
                  </span>
                  <p className="text-sm font-medium">
                    {selectedRequest.credit_note_number}
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">
                    Tipo
                  </span>
                  <p className="text-sm font-medium">
                    {RETURN_TYPE_LABELS[selectedRequest.return_type]}
                  </p>
                </div>
                <div className="space-y-1">
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">
                    Items
                  </span>
                  <p className="text-sm font-medium">
                    {selectedRequest.items?.length || 0} productos
                  </p>
                </div>
              </div>
              {selectedRequest.items && selectedRequest.items.length > 0 && (
                <div className="space-y-2">
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">
                    Productos a recibir
                  </span>
                  <div className="max-h-32 space-y-1 overflow-y-auto rounded border p-2">
                    {selectedRequest.items.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between text-sm"
                      >
                        <span>{item.product?.short_name || "-"}</span>
                        <span className="font-medium">
                          {item.expected_quantity} unidades
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => setClaimDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="secondary"
              onClick={() => confirmClaim(false)}
              disabled={claimMutation.isPending}
            >
              {claimMutation.isPending ? "Asignando..." : "Tomar sin Recepción"}
            </Button>
            <Button
              onClick={() => confirmClaim(true)}
              disabled={claimMutation.isPending}
            >
              <PackageX className="mr-2 h-4 w-4" />
              {claimMutation.isPending
                ? "Asignando..."
                : "Tomar e Iniciar Recepción"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}

function ReturnRequestRow({
  request,
  index,
  onClaim,
}: {
  request: ReturnRequest;
  index: number;
  onClaim: (request: ReturnRequest) => void;
}) {
  const itemsCount = request.items?.length || 0;
  const totalQuantity =
    request.items?.reduce((sum, item) => sum + item.expected_quantity, 0) || 0;

  return (
    <TableRow
      style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
      className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
    >
      <TableCell className="font-medium">{request.return_number}</TableCell>
      <TableCell>{request.client?.name || "-"}</TableCell>
      <TableCell>
        <Badge variant="outline">
          {RETURN_TYPE_LABELS[request.return_type]}
        </Badge>
      </TableCell>
      <TableCell>
        <div className="flex flex-col">
          <span className="font-medium">{itemsCount} productos</span>
          <span className="text-xs text-muted-foreground">
            {totalQuantity} unidades
          </span>
        </div>
      </TableCell>
      <TableCell>
        {request.credit_note_number ? (
          <Badge variant="secondary">{request.credit_note_number}</Badge>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
      </TableCell>
      <TableCell>
        <Badge variant={request.priority === 1 ? "destructive" : "secondary"}>
          {request.priority === 1
            ? "Urgente"
            : request.priority === 3
              ? "Baja"
              : "Normal"}
        </Badge>
      </TableCell>
      <TableCell>
        <Button size="sm" onClick={() => onClaim(request)}>
          Tomar
        </Button>
      </TableCell>
    </TableRow>
  );
}

export default ReturnsPage;
